import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { SerializedWorkRow } from './SerializedWorkRow';
import { partLabel, pointLabel } from '../lib/serializedWork';

export const SerializedWorkPicker = ({ value, onChange, machineryId, disabled, query, setQuery }) => {
  const [catalog, setCatalog] = useState({ components: [], machinery: [], points: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('all');
  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/serialized-components/work-options`).then(({ data }) => {
      if (active) { setCatalog(data); setError(''); }
    }).catch(() => { if (active) setError('Serialized parts could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);
  const chosen = new Set(value.flatMap((row) => [row.component.id, row.action === 'install' ? row.outgoing?.id : null]));
  const matches = catalog.components.filter((part) => {
    if (chosen.has(part.id)) return false;
    if (sourceFilter === 'inventory' && part.status === 'installed') return false;
    if (sourceFilter === 'installed' && part.status !== 'installed') return false;
    return `${partLabel(part)} ${pointLabel(part.current_installation_point_id, catalog)}`.toLowerCase().includes(query.toLowerCase().trim());
  });
  return (
    <div className="space-y-3 min-w-0" data-testid="serialized-work-picker">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Serialized parts</h3>
        <Button type="button" size="sm" variant="ghost" disabled={disabled || loading}
          data-testid="serialized-work-reload" onClick={() => { onChange([]); setReload((r) => r + 1); }}>Reload / reset selections</Button>
      </div>
      {error && <div role="alert" className="text-sm text-red-700" data-testid="serialized-work-error">{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px] gap-2 min-w-0">
        <Input data-testid="serialized-work-search" aria-label="Search serialized parts" value={query}
          onChange={(e) => setQuery(e.target.value)} disabled={disabled || loading}
          placeholder={loading ? 'Loading serialized parts…' : 'Search serial, part number, equipment…'} />
        <select data-testid="serialized-work-source-filter" aria-label="Serialized part source" value={sourceFilter}
          disabled={disabled} onChange={(e) => setSourceFilter(e.target.value)} className="min-w-0 rounded-md border px-2 py-2 text-sm">
          <option value="all">All locations</option><option value="inventory">Inventory</option><option value="installed">Installed</option>
        </select>
      </div>
      {!loading && !error && <div className="max-h-48 overflow-y-auto border rounded-md" data-testid="serialized-work-results">
        {matches.slice(0, 50).map((part) => <button type="button" key={part.id} disabled={disabled}
          data-testid={`serialized-work-option-${part.id}`} className="w-full text-left px-3 py-2 border-b last:border-0 hover:bg-slate-50 transition-colors"
          onClick={() => { onChange([...value, { component: part, action: 'associate', machinery_id: machineryId,
            installation_point_id: '', outgoing: null, outgoing_action: 'inventory', outgoing_status: '' }]); setQuery(''); }}>
          <span className="block text-sm break-words">{partLabel(part)}</span>
          <span className="block text-xs text-slate-600 break-words">{part.status.replaceAll('_', ' ')} · {pointLabel(part.current_installation_point_id, catalog)}
            {!['new', 'available', 'installed'].includes(part.status) && ' · History only — cannot install'}</span>
        </button>)}
        {matches.length === 0 && <p className="p-3 text-sm text-slate-500" data-testid="serialized-work-empty">No matching unselected serialized parts.</p>}
        {matches.length > 50 && <p className="p-2 text-xs" data-testid="serialized-work-more-results">Showing 50 of {matches.length} matches. Narrow your search.</p>}
      </div>}
      {value.map((row, index) => <SerializedWorkRow key={row.component.id} row={row} catalog={catalog} disabled={disabled}
        plannedOutgoing={row.outgoing && value.some((r) => r.component.id === row.outgoing.id && ['install', 'remove'].includes(r.action))}
        onChange={(updated) => onChange(value.map((r, i) => i === index ? updated : r))}
        onRemove={() => onChange(value.filter((_, i) => i !== index))} />)}
    </div>
  );
};